const policyDocuments = {
  "/payment-policy": {
    title: "Payment & billing policy",
    eyebrow: "Checkout & payment",
    description:
      "How payment authorisation, currencies, taxes, billing details, refunds, and payment security are handled.",
    sections: [
      {
        id: "payment-methods",
        title: "Accepted payment methods",
        paragraphs: [
          "The payment methods available for an order are shown at checkout. A displayed card brand or wallet is not a guarantee that every card, country, or transaction will be accepted by the payment provider.",
          "This storefront currently uses a demonstration payment hand-off. It does not collect card numbers on the {brand} page. A live store must identify its payment provider before accepting real payments.",
        ],
      },
      {
        id: "payment-authorisation",
        title: "Authorisation and capture",
        paragraphs: [
          "Submitting payment authorises the payment provider to verify the method and charge the total displayed at checkout. A pending authorisation may appear before an order is accepted. If an order is declined or cancelled, the provider or bank controls when a pending hold disappears.",
          "We may ask for reasonable verification or decline a transaction where billing details cannot be verified, the payment is refused, or fraud screening identifies a material risk.",
        ],
      },
      {
        id: "payment-pricing",
        title: "Currency, taxes, and charges",
        paragraphs: [
          "Prices are displayed in the currency identified beside the amount. Shipping, taxes, duties, packaging, discounts, and the final total are itemised at checkout where applicable.",
          "A bank or card issuer may apply conversion, international transaction, or other account fees. Those fees are imposed by the issuer and are not retained by {brand}.",
        ],
      },
      {
        id: "payment-refunds",
        title: "Refunds and billing corrections",
        paragraphs: [
          "Approved refunds are returned to the original payment method unless the law requires or permits another remedy. Bank processing times are outside our control. Store credit is used only where the customer agrees or where it is an appropriate remedy.",
          "If a displayed price or charged amount is wrong, contact us promptly. We will investigate and correct a verified billing error. Nothing in this policy limits a right or remedy that cannot lawfully be excluded.",
        ],
      },
    ],
  },
  "/order-policy": {
    title: "Order changes & cancellation policy",
    eyebrow: "Order help",
    description:
      "When orders may be changed or cancelled, and what happens when an item or order cannot be fulfilled.",
    sections: [
      {
        id: "order-receipt",
        title: "Order receipt and acceptance",
        paragraphs: [
          "An automated confirmation means we received the order request; it does not by itself mean the order has been accepted. Acceptance occurs when we confirm fulfilment or dispatch, subject to applicable law.",
          "We may contact you to verify information, resolve an address issue, or discuss an unavailable item before accepting the order.",
        ],
      },
      {
        id: "order-customer-changes",
        title: "Changes requested by you",
        paragraphs: [
          "Email {email} as soon as possible with the order number and requested change. We will try to update recipient details, gift messages, quantities, or delivery addresses before packing begins, but changes are not guaranteed after an order enters fulfilment.",
          "A carrier may charge for an address correction or reroute. We will disclose any known charge before asking you to approve it.",
        ],
      },
      {
        id: "order-cancellation",
        title: "Cancellation requests",
        paragraphs: [
          "You may request cancellation before dispatch. If the order has already been packed, personalised, transferred to a carrier, or includes a time-sensitive product, cancellation may no longer be possible. If it has shipped, the returns policy applies.",
          "A cancellation request does not remove any mandatory cancellation, refund, or consumer-guarantee right available under applicable law.",
        ],
      },
      {
        id: "order-store-cancellation",
        title: "Cancellation by us",
        paragraphs: [
          "We may cancel all or part of an order for an inventory error, unsafe or recalled product, pricing error, delivery restriction, failed payment, suspected fraud, or another legitimate fulfilment issue. We will explain the reason where reasonably possible.",
          "If we cancel an amount already paid, we will refund that amount to the original payment method. We will not substitute a materially different item without your agreement.",
        ],
      },
    ],
  },
  "/pricing-availability": {
    title: "Pricing & availability policy",
    eyebrow: "Product information",
    description:
      "How prices, product details, stock levels, substitutions, taxes, and display errors are managed.",
    sections: [
      {
        id: "pricing-display",
        title: "Displayed prices",
        paragraphs: [
          "Each product page identifies the price and currency for the selected item or variation. Unless stated otherwise, delivery, optional packaging, taxes, duties, and payment-provider charges are not included in the product price.",
          "Prices may change prospectively. A change does not alter an order already accepted, except where the law permits correction of an obvious error and the customer is offered a fair choice.",
        ],
      },
      {
        id: "pricing-stock",
        title: "Availability and quantity limits",
        paragraphs: [
          "Cart placement does not reserve inventory. Stock is allocated when an order is accepted. We may apply reasonable per-customer limits to scarce, seasonal, promotional, or safety-sensitive products.",
          "Natural and small-batch foods can vary by harvest, producer, packaging, colour, texture, and weight within lawful tolerances. Product labels and the item delivered control where a site image is illustrative.",
        ],
      },
      {
        id: "pricing-errors",
        title: "Errors and corrections",
        paragraphs: [
          "We aim to keep descriptions, availability, and pricing accurate. If a material error is found before fulfilment, we may correct it and ask whether you wish to continue, or cancel the affected item and issue a refund.",
          "We will not use this clause to avoid an accepted price merely because market conditions changed, or to limit rights arising from misleading information or other conduct covered by law.",
        ],
      },
      {
        id: "pricing-substitutions",
        title: "Substitutions",
        paragraphs: [
          "We do not replace an unavailable product with a materially different product without consent. Minor packaging changes that do not change the product, quantity, declared ingredients, allergen information, or price are not treated as substitutions.",
        ],
      },
    ],
  },
  "/preorders": {
    title: "Pre-order & backorder policy",
    eyebrow: "Future stock",
    description:
      "Rules for products specifically marked as pre-order, coming soon, or temporarily backordered.",
    sections: [
      {
        id: "preorder-scope",
        title: "When this policy applies",
        paragraphs: [
          "This policy applies only when a product is expressly offered for pre-order or backorder. A ‘coming soon’ label without an enabled purchase button is informational and does not reserve stock.",
          "The product page or checkout will state the expected availability window and whether payment is authorised, collected immediately, or collected closer to dispatch.",
        ],
      },
      {
        id: "preorder-estimates",
        title: "Release and dispatch estimates",
        paragraphs: [
          "Producer schedules, harvests, imports, inspections, and freight can change an estimate. We will communicate a material delay and provide an updated estimate where reasonably possible.",
          "If an order combines available and pre-order items, checkout or the order confirmation will explain whether items are expected to ship together or separately and whether extra delivery charges apply.",
        ],
      },
      {
        id: "preorder-changes",
        title: "Changes and cancellation",
        paragraphs: [
          "You may request cancellation of an unshipped pre-order by emailing {email}. If a material delay or product change occurs, we will offer any remedy required by law, which may include the choice to continue or cancel for a refund.",
          "We may cancel if a supplier cannot provide the product, import or safety requirements prevent sale, or the item is withdrawn. Any collected amount for the cancelled item will be refunded.",
        ],
      },
    ],
  },
  "/promotions-policy": {
    title: "Promotions & discount policy",
    eyebrow: "Offers & codes",
    description:
      "The general rules for promotional codes, discounts, free shipping offers, bundles, and limited-time campaigns.",
    sections: [
      {
        id: "promotion-eligibility",
        title: "Eligibility",
        paragraphs: [
          "Each promotion may include a start and end time, eligible products or customers, minimum spend, territory, redemption limit, and other clearly disclosed conditions. The specific offer terms and this policy apply together; the specific terms control if they conflict.",
          "Unless an offer says otherwise, codes are not transferable for resale, have no cash value, and cannot be applied to a completed order.",
        ],
      },
      {
        id: "promotion-redemption",
        title: "Using a promotion",
        paragraphs: [
          "Enter a code at the point shown before payment and confirm the discount appears in the order summary. Only one code may be used per order unless the offer expressly permits stacking.",
          "Minimum spend is calculated after excluded items and other discounts and before shipping and taxes, unless the offer states otherwise.",
        ],
      },
      {
        id: "promotion-returns",
        title: "Returns and partial cancellations",
        paragraphs: [
          "A refund normally reflects the amount actually paid for the returned item. If a return or cancellation causes the retained order to fall below an offer threshold, the refund may be adjusted only where that condition was clearly disclosed and the adjustment is lawful.",
          "A free gift may need to be returned unused if the qualifying purchase is returned, unless mandatory consumer rights require a different outcome.",
        ],
      },
      {
        id: "promotion-fair-use",
        title: "Fair use and corrections",
        paragraphs: [
          "We may refuse duplicated, automated, altered, publicly leaked, or fraudulently obtained codes, and may correct an obvious technical error. We will not withdraw a promotion from an accepted order merely because it was more popular than expected.",
        ],
      },
    ],
  },
  "/gift-cards-policy": {
    title: "Gift card & store credit policy",
    eyebrow: "Stored value",
    description:
      "Terms that apply if gift cards, vouchers, promotional credit, or store credit are offered for sale or issued.",
    sections: [
      {
        id: "gift-card-availability",
        title: "Availability and delivery",
        paragraphs: [
          "This policy applies only to a gift card, voucher, or store credit expressly issued by {brand}. The amount, currency, delivery method, activation requirements, and any lawful restrictions will be shown before purchase or when credit is issued.",
          "Electronic delivery times are estimates. The purchaser is responsible for providing an accurate recipient email address, and should contact us promptly if delivery does not arrive.",
        ],
      },
      {
        id: "gift-card-validity",
        title: "Validity and fees",
        paragraphs: [
          "A gift card sold to an Australian consumer will be valid for at least three years from purchase unless a statutory exception applies. Any expiry date will be clearly displayed. We do not charge a post-supply fee merely for holding or using a gift card.",
          "Promotional credit provided without payment may have a shorter, clearly disclosed period where permitted by law.",
        ],
      },
      {
        id: "gift-card-redemption",
        title: "Redemption",
        paragraphs: [
          "Gift cards may be used for eligible products on the channel and in the currency stated at issue. They cannot be used to buy another gift card, converted to cash, or used after cancellation, except where the law requires cash redemption or another remedy.",
          "If an order total exceeds the balance, the remainder must be paid using an accepted payment method. Any unused balance remains available until expiry.",
        ],
      },
      {
        id: "gift-card-loss",
        title: "Loss, misuse, and refunds",
        paragraphs: [
          "Treat a code like cash. Contact us promptly about suspected unauthorised use. We may replace a verifiable unused code at our discretion, but cannot promise replacement after value has been redeemed.",
          "A purchased gift card is not refundable for change of mind after delivery unless the offer says otherwise. This does not limit remedies for a faulty card, non-delivery, misdescription, or another failure covered by law.",
        ],
      },
    ],
  },
  "/food-safety": {
    title: "Food safety, allergens & storage policy",
    eyebrow: "Product care",
    description:
      "Important information about labels, allergens, dietary claims, storage, handling, shelf life, and food-safety questions.",
    sections: [
      {
        id: "food-labels",
        title: "Read the product label",
        paragraphs: [
          "Always read the ingredient list, allergen declaration, warning statements, use-by or best-before date, storage directions, and preparation instructions on the product delivered. Labels can change between batches, and the physical label controls if a site description differs.",
          "Contact {email} before ordering if you need current label information. Website copy is general product information and is not medical or dietary advice.",
        ],
      },
      {
        id: "food-allergens",
        title: "Allergens and cross-contact",
        paragraphs: [
          "Products may contain or be handled near common allergens, including cereals containing gluten, crustaceans, eggs, fish, milk, peanuts, soy, sesame, lupin, molluscs, sulphites, and tree nuts. Check the individual label every time, even if you have purchased the product before.",
          "A ‘may contain’ statement addresses possible unintended presence; it is not a complete statement of ingredients. People with a diagnosed allergy or coeliac disease should rely on current label information and advice from a qualified health professional.",
        ],
      },
      {
        id: "food-dietary-claims",
        title: "Dietary and origin claims",
        paragraphs: [
          "Terms such as vegan, vegetarian, gluten-free, organic, or a protected origin are used only when supported by the supplier or applicable certification. These claims do not guarantee an allergen-free environment unless the label expressly says so.",
          "Harvest year, origin, certification, and producer details may change with a new batch. We aim to update the product page, but customers with a material requirement should verify the delivered label before use.",
        ],
      },
      {
        id: "food-storage",
        title: "Storage and handling",
        paragraphs: [
          "Store unopened and opened products exactly as directed on the label. Inspect seals on arrival. Do not consume a product that is leaking, swollen, unusually pressurised, broken, recalled, past a use-by date, or otherwise appears unsafe.",
          "If a safety or quality issue is suspected, keep the product, lot or batch code, packaging, and proof of purchase; avoid consumption; and contact us promptly. For a medical emergency, contact local emergency services.",
        ],
      },
    ],
  },
  "/recalls": {
    title: "Product recall policy",
    eyebrow: "Product safety",
    description:
      "How product safety concerns and recalls are communicated, investigated, and resolved.",
    sections: [
      {
        id: "recall-response",
        title: "Our response to a recall",
        paragraphs: [
          "If we learn that a product may be unsafe, incorrectly labelled, affected by an undeclared allergen, or subject to a supplier or regulator recall, we will stop affected sales and follow applicable recall requirements.",
          "Where records allow, we will contact customers who purchased the affected batch. We may also publish a notice identifying the product, package size, batch or lot, date marking, hazard, affected locations, and required action.",
        ],
      },
      {
        id: "recall-customer-action",
        title: "What customers should do",
        paragraphs: [
          "Follow the recall notice immediately. Do not consume or give away an affected food. Depending on the notice, safely dispose of it or keep it isolated for return. A recall notice overrides ordinary return instructions.",
          "Contact a health professional or local emergency service if consumption may have caused illness or an allergic reaction.",
        ],
      },
      {
        id: "recall-remedy",
        title: "Refunds and replacements",
        paragraphs: [
          "We will provide the remedy stated in the recall notice and any additional remedy required by law. Proof of purchase may help us identify the transaction, but we will consider other reasonable evidence when records are unavailable.",
        ],
      },
      {
        id: "recall-report",
        title: "Report a concern",
        paragraphs: [
          "Email {email} with the product name, lot or batch code, best-before or use-by date, order number, photos, and a description of the issue. Keep the product and packaging unless it is unsafe to do so.",
        ],
      },
    ],
  },
  "/consumer-guarantees": {
    title: "Consumer guarantees policy",
    eyebrow: "Your statutory rights",
    description:
      "A plain-language statement that store policies do not replace consumer rights that apply by law.",
    sections: [
      {
        id: "guarantees-overview",
        title: "Rights that cannot be excluded",
        paragraphs: [
          "Our goods and services come with guarantees that cannot be excluded under applicable consumer law. For purchases covered by the Australian Consumer Law, this includes guarantees relating to acceptable quality, fitness for a disclosed purpose, matching description or sample, title, and supply within a reasonable time where no time is agreed.",
          "The voluntary {brand} satisfaction promise and every store policy operate in addition to these rights, not instead of them.",
        ],
      },
      {
        id: "guarantees-major",
        title: "Major problems",
        paragraphs: [
          "Where a product has a major problem, an eligible consumer may choose the remedy available under law, which can include rejecting the goods and choosing a refund or replacement. Compensation may also be available for reasonably foreseeable loss caused by the failure.",
        ],
      },
      {
        id: "guarantees-minor",
        title: "Other problems",
        paragraphs: [
          "Where a problem is not major, we may have the opportunity to provide an appropriate remedy within a reasonable time. For food and other goods that cannot safely or practically be repaired, the suitable remedy may be replacement or refund, depending on the circumstances and the law.",
        ],
      },
      {
        id: "guarantees-claim",
        title: "Making a claim",
        paragraphs: [
          "Contact {email} with a description of the problem and reasonable proof that the product was purchased from us. An order confirmation, payment record, or other evidence may be used; a particular form of receipt is not required where purchase can reasonably be shown.",
          "We may ask for photos, batch details, or return of a safe product so we can assess the issue, but will not impose a process that unlawfully prevents a valid claim.",
        ],
      },
    ],
  },
  "/reviews-policy": {
    title: "Reviews & community content policy",
    eyebrow: "Customer contributions",
    description:
      "Standards for ratings, reviews, photos, comments, recipes, and other customer-submitted material.",
    sections: [
      {
        id: "reviews-current-status",
        title: "Current storefront status",
        paragraphs: [
          "The ratings currently displayed in this storefront are labelled sample catalog data. No review is submitted, verified, or published by this front-end demonstration. A live review feature must identify how reviews are collected and verified.",
        ],
      },
      {
        id: "reviews-honesty",
        title: "Honest and relevant reviews",
        paragraphs: [
          "If reviews are enabled, submissions must reflect a genuine experience and relate to the relevant product or service. Do not make false claims, impersonate another person, disclose private information, or submit unlawful, abusive, discriminatory, dangerous, or infringing material.",
          "Incentivised reviews will be clearly disclosed and will not be required to be positive. We will not selectively suppress a review merely because it is critical.",
        ],
      },
      {
        id: "reviews-moderation",
        title: "Moderation",
        paragraphs: [
          "We may reject or remove spam, duplicate content, unsupported medical claims, irrelevant material, personal data, unlawful content, conflicts of interest, or content that violates these standards. Editing will be limited to formatting or redaction and will not change the review's meaning without permission.",
        ],
      },
      {
        id: "reviews-license",
        title: "Permission to use submitted content",
        paragraphs: [
          "You keep ownership of your original content. By submitting content for publication, you give {brand} a non-exclusive, worldwide, royalty-free licence to host, reproduce, format, display, and promote it in connection with the store, subject to applicable privacy and consumer law.",
          "You confirm that you have the rights needed to grant that permission. Contact {email} to report an intellectual-property, privacy, or safety concern.",
        ],
      },
    ],
  },
  "/marketing-policy": {
    title: "Marketing communications policy",
    eyebrow: "Email & messages",
    description:
      "How promotional email or messages require consent, identify the sender, and provide a working unsubscribe option.",
    sections: [
      {
        id: "marketing-consent",
        title: "Consent",
        paragraphs: [
          "We send marketing email or electronic messages only with consent or another lawful basis. A purchase does not automatically enrol you in unrelated marketing. Where consent is requested, the purpose and sender will be identified clearly.",
          "We keep appropriate records of consent. We do not use a marketing message itself to seek consent where that would be unlawful.",
        ],
      },
      {
        id: "marketing-content",
        title: "Sender details and content",
        paragraphs: [
          "Commercial messages will identify {brand}, provide current contact details, and distinguish advertising from essential service messages such as order, delivery, safety, or account notices.",
          "Offers remain subject to their displayed eligibility, duration, stock, and promotion terms.",
        ],
      },
      {
        id: "marketing-unsubscribe",
        title: "Unsubscribe",
        paragraphs: [
          "Every commercial electronic message will include a clear, functional, low-cost way to unsubscribe. For messages with an Australian link, we will action a valid request within five working days and keep the facility working for at least 30 days after sending, as required.",
          "You will not be required to create or sign into an account or provide unnecessary personal information to opt out. You may also email {email} from the address you want removed.",
        ],
      },
      {
        id: "marketing-service",
        title: "Service and safety messages",
        paragraphs: [
          "Opting out of marketing does not prevent necessary transactional, security, product-recall, or legal notices about a current order or service. Those messages will not include unrelated promotional content where consent is required.",
        ],
      },
    ],
  },
  "/accessibility": {
    title: "Accessibility statement",
    eyebrow: "Access for everyone",
    description:
      "Our commitment to accessible browsing, shopping, customer support, and policy information.",
    sections: [
      {
        id: "accessibility-commitment",
        title: "Our commitment",
        paragraphs: [
          "{brand} aims to provide a website that people can use with a range of devices, browsers, assistive technologies, and input methods. We work toward clear structure, keyboard access, visible focus, readable contrast, descriptive controls, text alternatives, and reduced-motion support.",
          "Accessibility is ongoing. Content, third-party services, and device combinations can change, so we test and improve the experience over time.",
        ],
      },
      {
        id: "accessibility-support",
        title: "Help with shopping or information",
        paragraphs: [
          "If a page, form, document, or checkout step creates a barrier, email {email}. Tell us the page, the task you were trying to complete, the technology you were using if you wish, and the format or assistance that would help.",
          "We will make reasonable efforts to provide the same information or service through an accessible alternative and to address the underlying issue.",
        ],
      },
      {
        id: "accessibility-feedback",
        title: "Feedback",
        paragraphs: [
          "Accessibility feedback is welcomed and will not affect your ability to shop or receive support. We aim to acknowledge a report promptly and will explain any practical limitation or planned fix where possible.",
        ],
      },
    ],
  },
  "/intellectual-property": {
    title: "Intellectual property policy",
    eyebrow: "Original work & rights",
    description:
      "How store content may be used and how to report a copyright, trademark, or other rights concern.",
    sections: [
      {
        id: "ip-ownership",
        title: "Ownership and permitted use",
        paragraphs: [
          "The {brand} name, original text, graphics, photographs, layout, code, and other store material are owned by {brand} or used with permission and are protected by applicable law. Product and producer names may belong to their respective owners.",
          "You may view the site and make a reasonable copy of order or policy information for personal, non-commercial use. No other licence is granted by implication.",
        ],
      },
      {
        id: "ip-restrictions",
        title: "Restricted uses",
        paragraphs: [
          "Without permission, do not republish, sell, falsely attribute, remove rights notices from, create a misleading endorsement with, or systematically copy protected store content. Facts, ideas, lawful quotation, and other uses permitted by law are not restricted by this policy.",
        ],
      },
      {
        id: "ip-report",
        title: "Report a rights concern",
        paragraphs: [
          "Send a good-faith report to {email}. Include your contact details; identification of the protected work or right; the exact page or material at issue; why you believe the use is unauthorised; and any authority you have to act for the owner.",
          "We may request clarification, preserve relevant records, restrict material while reviewing it, and notify the person who supplied it where appropriate. Knowingly false reports may cause harm, so consider obtaining legal advice if ownership or permitted use is uncertain.",
        ],
      },
      {
        id: "ip-brand-separation",
        title: "Independent brand statement",
        paragraphs: [
          "{brand} is presented as an independent brand. References to third-party products, producers, payment methods, or delivery services are descriptive and do not imply sponsorship, ownership, or endorsement unless expressly stated.",
        ],
      },
    ],
  },
  "/security": {
    title: "Security & fraud prevention policy",
    eyebrow: "Safe shopping",
    description:
      "Security practices, customer precautions, payment verification, vulnerability reports, and fraud review.",
    sections: [
      {
        id: "security-practices",
        title: "Security practices",
        paragraphs: [
          "We use reasonable administrative, technical, and physical safeguards appropriate to the store and the information handled. No internet transmission or storage system can be guaranteed completely secure.",
          "A production checkout should use encrypted transport and a vetted payment provider so full card details are not stored by {brand} unless expressly disclosed and properly secured.",
        ],
      },
      {
        id: "security-fraud-review",
        title: "Fraud review",
        paragraphs: [
          "Orders may be screened for unusual activity. We may request reasonable verification, limit quantities, delay fulfilment briefly, or decline an order where there is a material risk of unauthorised payment, reselling abuse, sanctions exposure, or other fraud.",
          "We do not ask for a full password, full card number, or one-time banking code by ordinary email. A legitimate verification request will be limited to what is reasonably necessary.",
        ],
      },
      {
        id: "security-customer",
        title: "Protecting yourself",
        paragraphs: [
          "Use a unique password for any future account service, keep devices updated, check the site address before entering information, and avoid payment links from unexpected messages. Contact us promptly about a suspicious message or unauthorised order.",
        ],
      },
      {
        id: "security-report",
        title: "Report a vulnerability",
        paragraphs: [
          "Send a concise, good-faith security report to {email}. Do not access another person's data, disrupt service, use destructive testing, demand payment through threats, or publish sensitive details before we have had a reasonable opportunity to investigate.",
        ],
      },
    ],
  },
  "/acceptable-use": {
    title: "Acceptable use policy",
    eyebrow: "Using the site",
    description:
      "Rules that protect shoppers, the store, service providers, and other people from misuse.",
    sections: [
      {
        id: "acceptable-lawful",
        title: "Lawful personal use",
        paragraphs: [
          "Use the site only for lawful browsing, communication, and purchasing. Provide accurate information and use only payment methods, addresses, and content that you are authorised to use.",
        ],
      },
      {
        id: "acceptable-prohibited",
        title: "Prohibited conduct",
        bullets: [
          "Do not attempt unauthorised access, bypass security or purchase limits, test stolen payment data, or interfere with another user's activity.",
          "Do not introduce malware, overload the service, scrape protected content at unreasonable scale, reverse engineer restricted systems, or evade technical controls.",
          "Do not submit unlawful, deceptive, threatening, discriminatory, privacy-invasive, or infringing content.",
          "Do not use promotions, returns, reviews, accounts, or customer support to commit fraud or obtain an unintended duplicate benefit.",
        ],
      },
      {
        id: "acceptable-enforcement",
        title: "Proportionate enforcement",
        paragraphs: [
          "We may restrict a feature, cancel an affected order, suspend access, preserve evidence, or report conduct where reasonably necessary to protect people, rights, systems, or legal obligations. We will not use this policy to prevent a good-faith complaint, security report, consumer claim, or lawful competition.",
        ],
      },
    ],
  },
  "/disclaimer": {
    title: "Website & product information disclaimer",
    eyebrow: "Important information",
    description:
      "Limits and context for editorial content, product information, recipes, links, availability, and technical access.",
    sections: [
      {
        id: "disclaimer-general",
        title: "General information",
        paragraphs: [
          "Recipes, journal articles, serving ideas, ingredient notes, and other editorial material are general information. They are not medical, nutrition, allergy, legal, or other professional advice.",
          "Follow current product labels and qualified professional advice for allergies, health conditions, pregnancy, medication interactions, and other individual needs.",
        ],
      },
      {
        id: "disclaimer-products",
        title: "Products and images",
        paragraphs: [
          "We aim for accurate descriptions and images, but packaging, harvest, colour, texture, and other natural characteristics may change. Screen settings can affect appearance. A material difference remains subject to the returns policy and applicable consumer law.",
        ],
      },
      {
        id: "disclaimer-links",
        title: "Third-party links and services",
        paragraphs: [
          "A link or integration does not mean we own or endorse a third-party service. The third party's terms and privacy practices apply to its service. We remain responsible for obligations that the law does not permit us to transfer or exclude.",
        ],
      },
      {
        id: "disclaimer-availability",
        title: "Site availability",
        paragraphs: [
          "We may maintain, update, or temporarily interrupt the site. We do not promise uninterrupted access, but this disclaimer does not exclude a guarantee, remedy, or liability that cannot lawfully be excluded.",
        ],
      },
    ],
  },
  "/corporate-gifts-policy": {
    title: "Corporate gifting policy",
    eyebrow: "Multi-recipient orders",
    description:
      "Terms for gift inquiries, quotes, recipient lists, personalisation, scheduling, substitutions, and business approvals.",
    sections: [
      {
        id: "corporate-inquiries",
        title: "Inquiries and quotes",
        paragraphs: [
          "A gift inquiry is not an accepted order. A quote will identify the products, quantities, packaging, personalisation, delivery assumptions, validity period, taxes, and payment schedule. An order is accepted only when the stated approval and payment steps are completed.",
          "The current website form is a front-end preview and does not transmit an inquiry. Email {email} for live assistance.",
        ],
      },
      {
        id: "corporate-recipient-data",
        title: "Recipient information",
        paragraphs: [
          "The customer must have authority to provide recipient names, addresses, messages, and other personal information for fulfilment. Use recipient data only for the agreed gift program and transfer it through the secure method we specify.",
          "We will use recipient information to prepare, deliver, support, and document the order, and handle it under the privacy policy.",
        ],
      },
      {
        id: "corporate-approval",
        title: "Approval and changes",
        paragraphs: [
          "The customer must approve spelling, addresses, product selection, branding, and any proof by the stated deadline. Changes after approval may affect price or schedule and require written confirmation.",
          "We do not make a material substitution without approval. Seasonal packaging or a substantially equivalent batch update may be made only where it does not change declared ingredients, allergens, quantity, or agreed value.",
        ],
      },
      {
        id: "corporate-delivery",
        title: "Delivery, returns, and undeliverable gifts",
        paragraphs: [
          "Delivery dates are estimates unless a quote expressly guarantees otherwise. The customer is responsible for complete recipient details. Extra carrier costs caused by an incorrect address will be disclosed and allocated as agreed in the quote.",
          "Personalised or perishable items may not be returnable for change of mind. Damaged, unsafe, misdescribed, or incorrectly supplied items remain covered by applicable consumer rights and the agreed order remedy.",
        ],
      },
    ],
  },
  "/wholesale-terms": {
    title: "Wholesale terms",
    eyebrow: "Trade customers",
    description:
      "Baseline terms for wholesale inquiries, quotes, minimums, resale, product information, delivery, and business accounts.",
    sections: [
      {
        id: "wholesale-application",
        title: "Applications and account approval",
        paragraphs: [
          "A wholesale inquiry does not create an account or require either party to trade. We may request reasonable business, resale, delivery, credit, and compliance information before offering wholesale terms.",
          "The current website form is a front-end preview and does not transmit an application. Email {email} for live assistance.",
        ],
      },
      {
        id: "wholesale-orders",
        title: "Quotes and orders",
        paragraphs: [
          "A quote or price list may state case packs, minimum order value, lead time, freight, taxes, payment timing, territory, and validity. An order is accepted only through the confirmation method stated in the trade documents.",
          "Wholesale pricing is confidential where marked and may change prospectively. A buyer may not deduct, set off, or short-pay an invoice unless agreed or allowed by law.",
        ],
      },
      {
        id: "wholesale-resale",
        title: "Resale and product information",
        paragraphs: [
          "Trade customers are responsible for lawful storage, rotation, display, resale, and any claims they independently make. Original labels, batch identification, date marking, warnings, and recall information must not be obscured or altered.",
          "Suggested resale prices are recommendations only unless a lawful written arrangement states otherwise. Nothing in these terms requires unlawful resale-price maintenance.",
        ],
      },
      {
        id: "wholesale-delivery",
        title: "Inspection, shortages, and safety",
        paragraphs: [
          "Inspect deliveries promptly and report visible shortages or transit damage with reasonable evidence. A short reporting target helps carrier claims but does not remove a right that cannot be excluded.",
          "A safety concern or recall must be acted on immediately. The buyer must preserve traceability records, stop affected sales, and cooperate with lawful recall instructions.",
        ],
      },
      {
        id: "wholesale-law",
        title: "Business terms and applicable law",
        paragraphs: [
          "Any credit, title, risk, warranty, liability, dispute, and governing-law provisions will be stated in the accepted trade agreement or invoice terms. Consumer guarantees may still apply to a business purchase where the law says they do.",
        ],
      },
    ],
  },
};

const policyCatalogGroups = [
  {
    id: "shopping",
    title: "Shopping & orders",
    description: "What happens before, during, and after checkout.",
    items: [
      { path: "/shipping", title: "Shipping & delivery", description: "Rates, timing, destinations, tracking, delays, and delivery issues." },
      { path: "/returns", title: "Returns & refunds", description: "Eligibility, damaged goods, return shipping, replacements, and refund timing." },
      { path: "/payment-policy", ...policyDocuments["/payment-policy"] },
      { path: "/order-policy", ...policyDocuments["/order-policy"] },
      { path: "/pricing-availability", ...policyDocuments["/pricing-availability"] },
      { path: "/preorders", ...policyDocuments["/preorders"] },
      { path: "/promotions-policy", ...policyDocuments["/promotions-policy"] },
      { path: "/gift-cards-policy", ...policyDocuments["/gift-cards-policy"] },
    ],
  },
  {
    id: "products",
    title: "Food & product care",
    description: "Safety, labels, allergens, recalls, and statutory remedies.",
    items: [
      { path: "/food-safety", ...policyDocuments["/food-safety"] },
      { path: "/recalls", ...policyDocuments["/recalls"] },
      { path: "/consumer-guarantees", ...policyDocuments["/consumer-guarantees"] },
    ],
  },
  {
    id: "privacy",
    title: "Privacy & trust",
    description: "How information, messages, payment risk, and browser storage are handled.",
    items: [
      { path: "/privacy", title: "Privacy policy", description: "Collection, use, disclosure, access, correction, complaints, and retention." },
      { path: "/cookies", title: "Cookie policy", description: "Cookies, local browser storage, current use, and available controls." },
      { path: "/security", ...policyDocuments["/security"] },
      { path: "/marketing-policy", ...policyDocuments["/marketing-policy"] },
    ],
  },
  {
    id: "site",
    title: "Site use & content",
    description: "The rules and commitments that apply when you use this website.",
    items: [
      { path: "/terms", title: "Terms & conditions", description: "The agreement governing use of the storefront and shopping services." },
      { path: "/acceptable-use", ...policyDocuments["/acceptable-use"] },
      { path: "/reviews-policy", ...policyDocuments["/reviews-policy"] },
      { path: "/accessibility", ...policyDocuments["/accessibility"] },
      { path: "/intellectual-property", ...policyDocuments["/intellectual-property"] },
      { path: "/disclaimer", ...policyDocuments["/disclaimer"] },
    ],
  },
  {
    id: "business",
    title: "Business programs",
    description: "Additional terms for gifting and trade inquiries.",
    items: [
      { path: "/corporate-gifts-policy", ...policyDocuments["/corporate-gifts-policy"] },
      { path: "/wholesale-terms", ...policyDocuments["/wholesale-terms"] },
    ],
  },
];

export { policyCatalogGroups, policyDocuments };
